import React, { useCallback, useMemo, useState } from 'react';
import date from 'date-and-time';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostTimeFieldProps {
  /** The time of the post now, ISO. */
  scheduledAt: string;
  label: string;
  help?: string;
  isSaving: boolean;
  /** Called with a new time ahead of now. */
  onSave: (at: Date) => void;
}

/**
 * "Change the time" in place (design b5): the time field and Save, no modal. A time in the past
 * is refused here, before the call.
 */
const PostTimeFieldComponent: React.FC<PostTimeFieldProps> = ({ scheduledAt, label, help, isSaving, onSave }) => {
  const intl = useIntl();
  const initial = useMemo(() => date.format(new Date(scheduledAt), 'YYYY-MM-DDTHH:mm'), [scheduledAt]);
  const [value, setValue] = useState(initial);
  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value), []);
  const picked = useMemo(() => {
    const at = new Date(value);
    return Number.isNaN(at.getTime()) ? null : at;
  }, [value]);
  const isFuture = picked !== null && picked.getTime() > Date.now();
  const save = useCallback(() => {
    if (picked !== null && isFuture) {
      onSave(picked);
    }
  }, [isFuture, onSave, picked]);

  return (
    <div className={styles.field}>
      <span className={styles.lbl}>{label}</span>
      <div className={styles.timefield}>
        <input
          type="datetime-local"
          className={styles.input}
          value={value}
          aria-label={label}
          disabled={isSaving}
          onChange={onChange}
        />
        <SMLButton size="sm" omitCap disabled={isSaving || !isFuture || value === initial} onClick={save}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.time.save', defaultMessage: 'Save' })}
        </SMLButton>
      </div>
      <span className={styles.tiny}>
        {value !== '' && !isFuture
          ? intl.formatMessage({
              id: 'app.sml.promote.post.reschedule.past',
              defaultMessage: 'Pick a time in the future.',
            })
          : help}
      </span>
    </div>
  );
};

export const PostTimeField = TypedMemo(PostTimeFieldComponent);
